import {
  resolveTenantConfig,
  isCatalogueOnly,
  type PublicEnvironment,
  type TenantConfig,
} from '@workspace/tenant-config/resolve'

export interface PublicFirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId: string | null
}

export interface PublicRuntimeConfig {
  schemaVersion: 1
  kind: 'public-runtime-config'
  tenant: string
  app: string
  environment: 'local' | 'development' | 'production'
  origin: string
  platformApiUrl: string
  communityApiUrl: string | null
  webSocketUrl: string
  assetsUrl: string
  supportEmail: string
  brandName: string
  themeId: string
  storagePrefix: string
  capabilities: Record<string, boolean | Record<string, boolean>>
  firebase: PublicFirebaseConfig | null
}

const FIREBASE_KEYS = [
  ['apiKey', 'VITE_FIREBASE_API_KEY'],
  ['authDomain', 'VITE_FIREBASE_AUTH_DOMAIN'],
  ['projectId', 'VITE_FIREBASE_PROJECT_ID'],
  ['storageBucket', 'VITE_FIREBASE_STORAGE_BUCKET'],
  ['messagingSenderId', 'VITE_FIREBASE_MESSAGING_SENDER_ID'],
  ['appId', 'VITE_FIREBASE_APP_ID'],
] as const

let current: TenantConfig | null = null

function installedConfig(): TenantConfig {
  if (!current) throw new Error('Public runtime config is not loaded')
  return current
}

export function installPublicConfig(env: PublicEnvironment): TenantConfig {
  current = resolveTenantConfig(env)
  return current
}

function requiredValue(env: PublicEnvironment, name: string): string {
  const value = env[name]
  if (typeof value !== 'string' || value.trim() === '') throw new Error(`${name} is not set`)
  return value.trim()
}

function capabilitiesFrom(env: PublicEnvironment): PublicRuntimeConfig['capabilities'] {
  const raw = requiredValue(env, 'VITE_CAPABILITIES')
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    throw new Error('VITE_CAPABILITIES must be an object of boolean capability flags')
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('VITE_CAPABILITIES must be an object of boolean capability flags')
  }
  const capabilities: PublicRuntimeConfig['capabilities'] = {}
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value === 'boolean') {
      capabilities[key] = value
      continue
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error('VITE_CAPABILITIES must be an object of boolean capability flags')
    }
    const nested: Record<string, boolean> = {}
    for (const [child, flag] of Object.entries(value)) {
      if (typeof flag !== 'boolean') throw new Error('VITE_CAPABILITIES must be an object of boolean capability flags')
      nested[child] = flag
    }
    capabilities[key] = nested
  }
  return capabilities
}

export function runtimeDocumentFromViteEnv(env: PublicEnvironment): PublicRuntimeConfig {
  const environment = requiredValue(env, 'VITE_ENVIRONMENT')
  if (environment !== 'local' && environment !== 'development' && environment !== 'production') {
    throw new Error('VITE_ENVIRONMENT must be local, development or production')
  }
  const capabilities = capabilitiesFrom(env)
  const community = capabilities.community === true
  let firebase: PublicFirebaseConfig | null = null
  const catalogueOnly = isCatalogueOnly(capabilities)
  if (environment !== 'local' && !catalogueOnly) {
    const measurement = env.VITE_FIREBASE_MEASUREMENT_ID
    firebase = {
      apiKey: requiredValue(env, 'VITE_FIREBASE_API_KEY'),
      authDomain: requiredValue(env, 'VITE_FIREBASE_AUTH_DOMAIN'),
      projectId: requiredValue(env, 'VITE_FIREBASE_PROJECT_ID'),
      storageBucket: requiredValue(env, 'VITE_FIREBASE_STORAGE_BUCKET'),
      messagingSenderId: requiredValue(env, 'VITE_FIREBASE_MESSAGING_SENDER_ID'),
      appId: requiredValue(env, 'VITE_FIREBASE_APP_ID'),
      measurementId: typeof measurement === 'string' && measurement.trim() ? measurement.trim() : null,
    }
  }
  const document: PublicRuntimeConfig = {
    schemaVersion: 1,
    kind: 'public-runtime-config',
    tenant: requiredValue(env, 'VITE_TENANT_ID'),
    app: 'apps/verde',
    environment,
    origin: requiredValue(env, 'VITE_WEBSITE_URL'),
    platformApiUrl: catalogueOnly ? '' : requiredValue(env, 'VITE_API_URL'),
    communityApiUrl: community ? requiredValue(env, 'VITE_COMMUNITY_API_URL') : null,
    webSocketUrl: catalogueOnly ? '' : requiredValue(env, 'VITE_WS_URL'),
    assetsUrl: typeof env.VITE_ASSETS_URL === 'string' ? env.VITE_ASSETS_URL : '',
    supportEmail: requiredValue(env, 'VITE_SUPPORT_EMAIL'),
    brandName: requiredValue(env, 'VITE_BRAND_NAME'),
    themeId: requiredValue(env, 'VITE_THEME_ID'),
    storagePrefix: requiredValue(env, 'VITE_STORAGE_PREFIX'),
    capabilities,
    firebase,
  }
  resolveTenantConfig(environmentFromRuntime(document))
  return document
}

function stringField(document: Record<string, unknown>, name: string, viteName: string): string {
  const value = document[name]
  if (typeof value !== 'string' || value.trim() === '') throw new Error(`${viteName} is not set`)
  return value.trim()
}

export function environmentFromRuntime(document: unknown): PublicEnvironment {
  if (!document || typeof document !== 'object' || Array.isArray(document)) {
    throw new Error('public runtime config must be an object')
  }
  const body = document as Record<string, unknown>
  if (body.schemaVersion !== 1 || body.kind !== 'public-runtime-config') {
    throw new Error('public runtime config schema or kind is wrong')
  }
  const environment = body.environment
  if (environment !== 'local' && environment !== 'development' && environment !== 'production') {
    throw new Error('VITE_ENVIRONMENT must be local, development or production')
  }
  if (!body.capabilities || typeof body.capabilities !== 'object' || Array.isArray(body.capabilities)) {
    throw new Error('VITE_CAPABILITIES must be an object of boolean capability flags')
  }
  const capabilities = capabilitiesFrom({ VITE_CAPABILITIES: JSON.stringify(body.capabilities) })
  const catalogueOnly = isCatalogueOnly(capabilities)
  const env: PublicEnvironment = {
    VITE_TENANT_ID: stringField(body, 'tenant', 'VITE_TENANT_ID'),
    VITE_BRAND_NAME: stringField(body, 'brandName', 'VITE_BRAND_NAME'),
    VITE_THEME_ID: stringField(body, 'themeId', 'VITE_THEME_ID'),
    VITE_STORAGE_PREFIX: stringField(body, 'storagePrefix', 'VITE_STORAGE_PREFIX'),
    VITE_WEBSITE_URL: stringField(body, 'origin', 'VITE_WEBSITE_URL'),
    VITE_SUPPORT_EMAIL: stringField(body, 'supportEmail', 'VITE_SUPPORT_EMAIL'),
    VITE_ENVIRONMENT: environment,
    VITE_API_URL: catalogueOnly ? '' : stringField(body, 'platformApiUrl', 'VITE_API_URL'),
    VITE_WS_URL: catalogueOnly ? '' : stringField(body, 'webSocketUrl', 'VITE_WS_URL'),
    VITE_ASSETS_URL: typeof body.assetsUrl === 'string' ? body.assetsUrl : '',
    VITE_CAPABILITIES: JSON.stringify(body.capabilities),
  }
  if (typeof body.communityApiUrl === 'string' && body.communityApiUrl.trim()) {
    env.VITE_COMMUNITY_API_URL = body.communityApiUrl.trim()
  }
  if (environment !== 'local' && !catalogueOnly) {
    const firebase = body.firebase
    if (!firebase || typeof firebase !== 'object' || Array.isArray(firebase)) {
      throw new Error('VITE_FIREBASE_API_KEY is not set')
    }
    const web = firebase as Record<string, unknown>
    for (const [field, viteName] of FIREBASE_KEYS) {
      const value = web[field]
      if (typeof value !== 'string' || value.trim() === '') throw new Error(`${viteName} is not set`)
      env[viteName] = value.trim()
    }
    if (typeof web.measurementId === 'string' && web.measurementId.trim()) {
      env.VITE_FIREBASE_MEASUREMENT_ID = web.measurementId.trim()
    }
  }
  return env
}

export async function loadPublicConfig(): Promise<TenantConfig> {
  const response = await fetch('/runtime-config.json', { cache: 'no-store' })
  if (!response.ok) throw new Error('public runtime config could not be loaded')
  let document: unknown
  try {
    document = await response.json()
  } catch {
    throw new Error('public runtime config is not JSON')
  }
  return installPublicConfig(environmentFromRuntime(document))
}

export const tenant: TenantConfig = new Proxy({} as TenantConfig, {
  get(_target, property) {
    if (typeof property === 'symbol') return undefined
    return installedConfig()[property as keyof TenantConfig]
  },
  has(_target, property) {
    return property in installedConfig()
  },
  ownKeys() {
    return Reflect.ownKeys(installedConfig())
  },
  getOwnPropertyDescriptor(_target, property) {
    const config = installedConfig()
    if (!Object.prototype.hasOwnProperty.call(config, property)) return undefined
    return {
      configurable: true,
      enumerable: true,
      value: config[property as keyof TenantConfig],
    }
  },
})

export function storageKey(name: string): string {
  return `${installedConfig().storagePrefix}${name}`
}
